import { Fragment, useEffect, useState } from 'react';
import { can, formatJalali, formatRial } from '@kia/shared';
import { readBase64 } from '../ai/attachments';
import { api, errMsg, getAuthToken } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { rowButton } from '../lib/rowButton';
import { useApiResource } from '../lib/useApiResource';
import {
  Button,
  Card,
  ErrorRetry,
  Field,
  Input,
  JalaliDateInput,
  NumberInput,
  useToast,
} from '../ui';

/**
 * پیش‌دریافت‌ها (CIRCULARITY_360 Phase B3) — customer deposits / advances expected BEFORE delivery.
 * An expected deposit projects onto the cash calendar as an estimated INFLOW on its date; marking it
 * received (with an optional receipt photo) stops it projecting; cancelling drops it. Read-only for
 * users without `cashflow.write`.
 */

const STATUS_FA: Record<string, string> = {
  expected: 'در انتظار',
  received: 'دریافت‌شده',
  cancelled: 'لغوشده',
};

interface Deposit {
  id: number;
  customer_id: number | null;
  customer_name: string | null;
  label: string;
  amount: number;
  expected_date: string;
  status: 'expected' | 'received' | 'cancelled';
  proof_doc_id: number | null;
}
interface Customer {
  id: number;
  name: string;
}

export function DepositsPanel() {
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user ? can(user.role, 'cashflow.write') : false;
  const { data, status, error, reload } = useApiResource<Deposit[]>('/api/deposits');
  const deposits = data ?? [];
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const [customerId, setCustomerId] = useState('');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState(0);
  const [expectedDate, setExpectedDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<Customer[]>('/api/customers').then((r) => {
      if (r.ok) setCustomers(r.body as Customer[]);
    });
  }, []);

  async function submit() {
    if (!(amount > 0) || !expectedDate) {
      toast('مبلغ و تاریخ انتظار لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api('/api/deposits', {
      method: 'POST',
      body: JSON.stringify({
        customer_id: customerId ? Number(customerId) : null,
        label,
        amount,
        expected_date: expectedDate,
      }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r, 'خطا در ثبت'), 'error');
    toast('پیش‌دریافت ثبت شد ✓');
    setAdding(false);
    setLabel('');
    setAmount(0);
    setExpectedDate('');
    setCustomerId('');
    reload();
  }

  async function setStatus(d: Deposit, status: 'received' | 'cancelled') {
    setBusy(true);
    const proof =
      status === 'received' && file
        ? { filename: file.name, mime: file.type, dataBase64: await readBase64(file) }
        : undefined;
    const r = await api(`/api/deposits/${d.id}`, {
      method: 'POST',
      body: JSON.stringify({ status, proof }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r, 'خطا در به‌روزرسانی'), 'error');
    toast(status === 'received' ? 'دریافت ثبت شد ✓' : 'لغو شد');
    setFile(null);
    setOpenId(null);
    reload();
  }

  return (
    <Card title="پیش‌دریافت‌ها و مساعده‌ها">
      <p className="muted" style={{ marginTop: 0 }}>
        مبالغی که پیش از تحویل انتظار می‌رود (مثلاً پیش‌پرداخت قرارداد) به‌صورت ورودی برآوردی در
        «تقویم نقدینگی» لحاظ می‌شوند. با ثبت دریافت (همراه عکس رسید)، از پیش‌بینی حذف و در موجودی
        لحاظ می‌شود.
      </p>

      {canWrite ? (
        adding ? (
          <div
            className="row"
            style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}
          >
            <Field label="مشتری (اختیاری)">
              <select
                className="input"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <option value="">—</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="شرح">
              <Input value={label} onChange={(e) => setLabel(e.target.value)} />
            </Field>
            <Field label="مبلغ (﷼)">
              <div style={{ width: 170 }}>
                <NumberInput value={amount} onValue={setAmount} />
              </div>
            </Field>
            <Field label="تاریخ انتظار">
              <JalaliDateInput value={expectedDate} onValue={setExpectedDate} />
            </Field>
            <Button onClick={submit} loading={busy}>
              ثبت
            </Button>
            <Button variant="secondary" onClick={() => setAdding(false)} disabled={busy}>
              انصراف
            </Button>
          </div>
        ) : (
          <Button variant="secondary" onClick={() => setAdding(true)} style={{ marginBottom: 12 }}>
            + پیش‌دریافت جدید
          </Button>
        )
      ) : null}

      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : deposits.length === 0 ? (
        <p className="muted">پیش‌دریافتی ثبت نشده.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>مشتری / شرح</th>
                <th>مبلغ</th>
                <th>تاریخ انتظار</th>
                <th>وضعیت</th>
                <th>رسید</th>
              </tr>
            </thead>
            <tbody>
              {deposits.map((d) => (
                <Fragment key={d.id}>
                  <tr
                    {...(canWrite && d.status === 'expected'
                      ? rowButton(() => setOpenId(openId === d.id ? null : d.id))
                      : {})}
                    style={{
                      cursor: canWrite && d.status === 'expected' ? 'pointer' : 'default',
                      opacity: d.status === 'cancelled' ? 0.5 : 1,
                    }}
                  >
                    <td>
                      {canWrite && d.status === 'expected' ? (openId === d.id ? '▾ ' : '▸ ') : ''}
                      {d.customer_name || d.label || '—'}
                    </td>
                    <td className="num">{formatRial(d.amount)}</td>
                    <td>{formatJalali(d.expected_date)}</td>
                    <td
                      style={{
                        color:
                          d.status === 'received'
                            ? 'var(--green)'
                            : d.status === 'cancelled'
                              ? 'var(--muted)'
                              : undefined,
                      }}
                    >
                      {STATUS_FA[d.status]}
                    </td>
                    <td>
                      {d.proof_doc_id ? (
                        <a
                          href={`/api/documents/${d.proof_doc_id}/raw?t=${getAuthToken() ?? ''}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          📎 مشاهده
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                  {openId === d.id && canWrite && d.status === 'expected' ? (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--panel, #fafafa)' }}>
                        <div
                          className="row"
                          style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}
                        >
                          <Field label="عکس رسید (اختیاری)">
                            <input
                              className="input"
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                            />
                          </Field>
                          <Button onClick={() => setStatus(d, 'received')} loading={busy}>
                            ثبت دریافت
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => setStatus(d, 'cancelled')}
                            disabled={busy}
                          >
                            لغو پیش‌دریافت
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
